import type { ComponentProps } from 'react'
import { useCallback, useContext, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import { StandaloneFlightMenu } from 'contexts/Flights/FlightSearchWidget/types'
import { FlightSearchWidgetActions } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import FlightSearchWidgetStateContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateContext'
import FlightSearchWidgetDispatchContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetDispatchContext'
import FlightSearchTravellerSelectContent from '../FlightTravellerSelect/FlightSearchTravellerSelectContent'
import FlightSearchTravellerSelectField from '../FlightTravellerSelect/FlightSearchTravellerSelectField'
import DropdownSheet from 'LuxKit/Dropdown/Sheet/DropdownSheet'

interface Props {
  anchorRef?: ComponentProps<typeof DropdownSheet>['anchorRef']
  className?: string
  disabled?: boolean
}

function FlightSearchTravellerSelect(props: Props) {
  const { className, anchorRef, disabled } = props
  const intl = useIntl()
  const state = useContext(FlightSearchWidgetStateContext)
  const dispatch = useContext(FlightSearchWidgetDispatchContext)

  const { activeMenu } = state

  const setActiveMenu = useCallback((activeMenu?: StandaloneFlightMenu) => {
    dispatch({ type: FlightSearchWidgetActions.SET_ACTIVE_MENU, activeMenu })
  }, [dispatch])

  const onApply = useCallback(() => {
    setActiveMenu()
  }, [setActiveMenu])

  const onCancel = useCallback(() => {
    setActiveMenu()
  }, [setActiveMenu])

  const handleOpen = useCallback(() => {
    setActiveMenu(activeMenu === 'occupants' ? undefined : StandaloneFlightMenu.occupants)
  }, [setActiveMenu, activeMenu])

  const triggerRef = useRef<HTMLButtonElement>(null)

  const applyText = useMemo(
    () =>
      intl.formatMessage({
        id: 'XV_uWUPjmcqL5r9jwE-x',
        defaultMessage: 'Apply',
        description: 'Button text to apply traveller selection in flight search',
      }),
    [intl],
  )

  return (
    <div className={className}>
      <FlightSearchTravellerSelectField
        onClick={handleOpen}
        ref={triggerRef}
        disabled={!!disabled}
        data-testid="travellers"
      />

      <DropdownSheet
        size="M"
        open={activeMenu === StandaloneFlightMenu.occupants}
        onClose={onCancel}
        anchorRef={anchorRef ?? triggerRef}
        triggerRef={triggerRef}
        primaryActionProps={{
          'data-testid': 'apply-flight-search-changes',
          type: 'submit',
          children: applyText,
          onClick: onApply,
        }}
      >
        <FlightSearchTravellerSelectContent />
      </DropdownSheet>
    </div>
  )
}

export default FlightSearchTravellerSelect
